import React from 'react';
import {
    ClientContext,
    patientServiceEvents,
    ReminderContext,
    ReminderContextType,
} from '~/services';
import { PaginationType, useServiceSubscribe } from '~/util';
import { BackButton } from '~/components/BackButton';
import { ResultsHeader } from '~/components/SearchResults';
import { SpinnerOverlay } from '~/components/SpinnerOverlay';
import { Pagination } from '~/components/Pagination';
import {
    ReminderFilterBadges,
    ReminderFiltersModal,
    ReminderRow,
    ReminderStatistics,
    useReminderQuery,
} from '~/components/Reminders';

export function RemindersPage() {
    const { reportYear } = React.useContext(ClientContext);
    const { paginationRef, sort, updateSort } = React.useContext(
        ReminderContext
    ) as ReminderContextType;
    const [loading, setLoading] = React.useState(false);
    const [reminderFeed, setReminderFeed] = React.useState([]);
    const { getReminders } = useReminderQuery();
    const loadReminders = React.useCallback(async () => {
        setLoading(true);
        const results = await getReminders();
        setReminderFeed(results);
        setLoading(false);
    }, [getReminders]);

    React.useEffect(() => {
        loadReminders();
    }, [reportYear, loadReminders]);

    function updatePagination(pagination: PaginationType) {
        paginationRef.current = { ...pagination };
        loadReminders();
    }

    useServiceSubscribe(patientServiceEvents.reminders_updated, loadReminders);

    return (
        <>
            <ReminderStatistics />
            <div className="card">
                <div className="card-header align-items-center d-flex justify-content-between">
                    <div>
                        <BackButton />
                        <span className="h4">Reminders</span>
                    </div>
                    <ReminderFiltersModal />
                </div>
                <div className="card-body">
                    <div className="row align-items-center">
                        <ReminderFilterBadges />
                    </div>
                    {!loading && reminderFeed.length === 0 ? (
                        <div className="text-center mt-5 mb-5">
                            <p>
                                There are no results
                                <br />
                                for your current search
                            </p>
                        </div>
                    ) : (
                        <div className="search-patient-content ">
                            <SpinnerOverlay show={loading} />
                            <div className="table-scroll search-patient-scroll reminders-table">
                                <table className="table table-striped table-bordered fixed-table">
                                    <thead className="table-dark">
                                        <tr>
                                            <ResultsHeader
                                                field="p.first_name"
                                                label="First Name"
                                                sort={sort}
                                                updateSort={updateSort}
                                                className="th-9"
                                            />
                                            <ResultsHeader
                                                field="p.last_name"
                                                label="Last Name"
                                                sort={sort}
                                                updateSort={updateSort}
                                                className="th-9"
                                            />
                                            <ResultsHeader
                                                field="p.dob"
                                                label="DOB"
                                                sort={sort}
                                                updateSort={updateSort}
                                                className="th-9"
                                            />
                                            <ResultsHeader
                                                field="due_at"
                                                label="Due Date"
                                                sort={sort}
                                                updateSort={updateSort}
                                                className="th-9"
                                            />
                                            <ResultsHeader
                                                field="description"
                                                label="Action"
                                                sort={sort}
                                                className="th-16"
                                            />
                                            <ResultsHeader
                                                field="status"
                                                label="Status"
                                                sort={sort}
                                                className="th-10"
                                            />
                                            <th className="th-3">
                                                <span className="sr-only">Actions</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reminderFeed.map((reminder: ReminderType) => (
                                            <ReminderRow
                                                reminder={reminder}
                                                key={`${reminder.id}${reminder.status}${reminder.due_at}${reminder.description}`}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <Pagination
                                pagination={paginationRef.current}
                                updatePagination={updatePagination}
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
